import { PRODUCT_CONFIG } from "@stb/product-config"
import { createServerFn } from "@tanstack/react-start"
import { and, eq, inArray } from "drizzle-orm"
import { z } from "zod"

import { getSessionUser } from "@/auth/session.server"
import {
  hasEarlierSubscription,
  hasStripeCustomer,
  LIVE_STRIPE_STATUSES,
} from "@/billing/billing-state.server"
import { subscriptions } from "@/billing/db/schema"
import { EXTRA_SEATS_KEY, TEAM_BASE_KEY } from "@/billing/entitlements.server"
import { getOrganisationEntitlements } from "@/billing/organisation-entitlements.server"
import { countOccupiedSeats } from "@/billing/seat-usage.server"
import { isTeamOwner } from "@/billing/team-seats.server"
import { isAppError } from "@/core/app-error"
import { getMainDb } from "@/db/main/db.server"
import { isTeamsEnabled, requireManager } from "@/organisations/teams.server"

import type { TeamBillingState } from "../teams/types"

const input = z.object({ organisationId: z.uuid() })

// logger.server is imported inside the handler: a top-level use would keep it in the client bundle
const getLog = async () =>
  (await import("@/core/logger.server")).logger.child({ module: "team-billing" })

/**
 * What the Members and Billing pages need to show seats (T506, T507): seats allowed, seats in use
 * (every active non-Owner member plus pending invitations, the same count the invite and seat
 * change checks use) and, for the Owner only, the plan itself. Owner and Admin may read the seat
 * numbers; payment details (status, interval, trial) go to the Owner alone. Read-only.
 */
export const getTeamBilling = createServerFn({ method: "GET" })
  .validator((data: z.input<typeof input>) => input.parse(data))
  .handler(async ({ data }): Promise<TeamBillingState> => {
    if (!isTeamsEnabled()) throw new Error("Teams is not available")
    const user = await getSessionUser()
    if (!user) throw new Error("Sign in to continue.")
    const db = getMainDb()
    try {
      await requireManager(db, data.organisationId, user.id)
      const [entitlements, occupied] = await Promise.all([
        getOrganisationEntitlements(db, data.organisationId),
        countOccupiedSeats(db, data.organisationId),
      ])
      const state: TeamBillingState = {
        viewerUserId: user.id,
        seatLimit: entitlements.teamSeats,
        occupiedSeats: occupied,
        hasTeamPlan: entitlements.features.includes("teams"),
        plan: null,
        trialEligible: false,
        canOpenPortal: false,
      }
      if (!(await isTeamOwner(db, data.organisationId, user.id))) return state
      const rows = await db
        .select({
          status: subscriptions.status,
          interval: subscriptions.interval,
          trialEnd: subscriptions.trialEnd,
          currentPeriodEnd: subscriptions.currentPeriodEnd,
          cancelAtPeriodEnd: subscriptions.cancelAtPeriodEnd,
          addons: subscriptions.addons,
        })
        .from(subscriptions)
        .where(
          and(
            eq(subscriptions.organisationId, data.organisationId),
            eq(subscriptions.provider, "stripe"),
            inArray(subscriptions.status, [...LIVE_STRIPE_STATUSES]),
          ),
        )
      const row = rows.find((r) => (r.addons[TEAM_BASE_KEY] ?? 0) > 0)
      return {
        ...state,
        plan: row
          ? {
              status: row.status,
              // what the subscription holds, which can differ from `seatLimit` while the plan is not in force
              totalSeats: PRODUCT_CONFIG.team.includedSeats + (row.addons[EXTRA_SEATS_KEY] ?? 0),
              interval: row.interval === "year" ? "year" : "month",
              trialEnd: row.trialEnd?.toISOString() ?? null,
              currentPeriodEnd: row.currentPeriodEnd?.toISOString() ?? null,
              cancelAtPeriodEnd: row.cancelAtPeriodEnd,
            }
          : null,
        trialEligible: !(await hasEarlierSubscription(db, data.organisationId)),
        canOpenPortal: await hasStripeCustomer(db, data.organisationId),
      }
    } catch (error) {
      if (isAppError(error)) throw new Error(error.message, { cause: error })
      ;(await getLog()).error("team billing lookup failed", {
        operation: "get_team_billing",
        user_id: user.id,
        organisation_id: data.organisationId,
        error,
      })
      throw new Error("Something went wrong. Please try again.", { cause: error })
    }
  })
